import { useState } from 'react';
import type { HeatingAssessment } from './buildPortalViewModel';
import { RetentionScale } from './RetentionScale';

interface HeatingSectionProps {
  data: HeatingAssessment;
}

export function HeatingSection({ data }: HeatingSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const [assumptionsExpanded, setAssumptionsExpanded] = useState(false);

  return (
    <div className="portal-section portal-section--heating">
      <button
        className="portal-section__toggle"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <h3 className="portal-section__title">Heating system</h3>
        <span className="portal-section__chevron" aria-hidden="true">
          {expanded ? '\u25B4' : '\u25BE'}
        </span>
      </button>

      {expanded && (
        <div className="portal-section__content">
          <div className="portal-section__row">
            <dt>Heat source</dt>
            <dd>{data.heatSourceType}</dd>
          </div>

          <div className="portal-section__row">
            <dt>Flow temperature</dt>
            <dd>
              {data.flowTemperature.label}
              {data.flowTemperature.constraint && (
                <span className="portal-section__constraint">
                  {data.flowTemperature.constraint}
                </span>
              )}
            </dd>
          </div>

          {data.distributionConstraints.length > 0 && (
            <div className="portal-section__row">
              <dt>Distribution constraints</dt>
              <dd>
                <ul className="portal-section__constraint-list">
                  {data.distributionConstraints.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </dd>
            </div>
          )}

          <div className="portal-section__row">
            <dt>Efficiency context</dt>
            <dd>{data.efficiencyContext}</dd>
          </div>

          {/* Quantified heat loss summary (simple model) */}
          {data.heatLossSummary && (
            <div className="portal-section__row portal-section__row--full">
              <dt>Property heat loss</dt>
              <dd>
                <p>
                  Design heat loss:{' '}
                  <strong>{data.heatLossSummary.totalHeatLossKW.toFixed(1)} kW</strong>
                  {' '}at {data.heatLossSummary.designTempExternalC}°C.
                  Recommended sizing target:{' '}
                  <strong>{data.heatLossSummary.sizingTargetKW.toFixed(1)} kW</strong>
                  {' '}(includes 20% headroom).
                </p>
                <p>
                  Sizing confidence:{' '}
                  <strong>{data.heatLossSummary.confidenceBand}</strong>
                  {' '}({data.heatLossSummary.confidence0to100}%)
                  {data.heatLossSummary.confidence0to100 < 60 && (
                    <span style={{ color: '#c0392b', marginLeft: 8 }} role="alert">
                      <span aria-hidden="true">⚠</span> Verify before final specification.
                    </span>
                  )}
                </p>
                {data.heatLossSummary.notes.length > 0 && (
                  <div>
                    <button
                      className="portal-section__toggle"
                      onClick={() => setAssumptionsExpanded(!assumptionsExpanded)}
                      aria-expanded={assumptionsExpanded}
                      style={{ fontSize: '0.85em', marginTop: 4 }}
                    >
                      {assumptionsExpanded ? 'Hide assumptions ▴' : 'Show assumptions ▾'}
                    </button>
                    {assumptionsExpanded && (
                      <ul className="portal-section__constraint-list">
                        {data.heatLossSummary.notes.map((n, i) => (
                          <li key={i}>{n}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </dd>
            </div>
          )}

          {/* Add Retention Scale visualization if heat loss data is available */}
          {data.heatLoss && (
            <div className="portal-section__row portal-section__row--full">
              <RetentionScale 
                currentValue={data.heatLoss.current} 
                targetValue={data.heatLoss.target} 
                label="Building Thermal Retention"
              />
            </div>
          )}

          {data.heatPumpViability && (
            <div className="portal-section__hp-viability">
              <h4 className="portal-section__hp-title">
                Heat pump viability: {data.heatPumpViability.viable ? 'Conditionally viable' : 'Constrained'}
              </h4>
              {data.heatPumpViability.constraints.length > 0 && (
                <div>
                  <strong>Constraints:</strong>
                  <ul className="portal-section__constraint-list">
                    {data.heatPumpViability.constraints.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}
              {data.heatPumpViability.enablers.length > 0 && (
                <div>
                  <strong>What would unlock it (A+):</strong>
                  <ul className="portal-section__enabler-list">
                    {data.heatPumpViability.enablers.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
